import { AlertTriangle, Check, Circle, ShieldCheck, ShieldQuestion, RotateCcw, Cpu } from 'lucide-react';
import type { PassportVerification } from '../dto/telemetry-passport.js';
import type { VerificationChecksPresentation } from '../verification/presentation.js';
import {
  TIER_3_SELF_REPORTED_LABEL,
  verificationFailureSymptom,
  multiGpuCaveat,
  COMPUTE_CORRECTNESS_PANEL_HEADING,
  COMPUTE_CORRECTNESS_PASS_LABEL,
  COMPUTE_CORRECTNESS_PASS_DETAIL,
  COMPUTE_CORRECTNESS_FAIL_LABEL,
  COMPUTE_CORRECTNESS_FAIL_DETAIL,
  COMPUTE_CORRECTNESS_FOOTNOTE,
  vramPanelHeading,
  vramPassLabel,
  vramPassDetail,
  vramFailLabel,
  vramFailDetail,
  vramFootnote,
  type VerificationHardwarePresentation,
} from '../verification/presentation.js';
import { formatDate, formatBytes } from './format.js';

/**
 * SIL-613 — Compute Correctness + VRAM Pattern Test panels, rendered on
 * EVERY non-'none' verification outcome (all tiers). Hard presentation
 * requirements (see presentation.ts's copy-constants header, verbatim
 * product review requirements, not style choices):
 *
 *   1. No aggregate badge — these two panels are rendered separately, never
 *      combined into a single pass/fail state.
 *   2. Different weight for the two passes — compute pass uses a checkmark
 *      (<Check>); VRAM pass deliberately does NOT (<Circle> — a neutral,
 *      non-affirming marker), because VRAM coverage is an unknowable
 *      fraction of the card and a checkmark there would read as "fully
 *      verified" the same way compute's checkmark correctly does.
 *   3. Fails get identical full-weight treatment for both (AlertTriangle,
 *      red).
 *   4. Separate panels, compute first, each with its own heading and
 *      co-located footnote.
 */
function ComputeCorrectnessPanel({ compute }: { compute: VerificationChecksPresentation['compute'] }) {
  if (!compute.performed) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/30 p-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {COMPUTE_CORRECTNESS_PANEL_HEADING}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Not run for this verification run.</p>
      </div>
    );
  }
  return (
    <div className={`rounded-md border p-3 ${compute.passed ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {COMPUTE_CORRECTNESS_PANEL_HEADING}
      </div>
      <div className={`mt-1 flex items-center gap-1.5 text-sm font-medium ${compute.passed ? 'text-emerald-800' : 'text-red-800'}`}>
        {compute.passed ? <Check className="size-4" aria-hidden /> : <AlertTriangle className="size-4" aria-hidden />}
        {compute.passed ? COMPUTE_CORRECTNESS_PASS_LABEL : COMPUTE_CORRECTNESS_FAIL_LABEL}
      </div>
      <p className={`mt-1 text-xs ${compute.passed ? 'text-emerald-700' : 'text-red-700'}`}>
        {compute.passed ? COMPUTE_CORRECTNESS_PASS_DETAIL : COMPUTE_CORRECTNESS_FAIL_DETAIL}
      </p>
      <p className="mt-1.5 text-[11px] text-muted-foreground">{COMPUTE_CORRECTNESS_FOOTNOTE}</p>
    </div>
  );
}

function VramPatternPanel({ vram }: { vram: VerificationChecksPresentation['vram'] }) {
  const bytes = formatBytes(vram.bytesTested);
  if (!vram.performed) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/30 p-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">VRAM Pattern Test</div>
        <p className="mt-1 text-xs text-muted-foreground">Not run for this verification run.</p>
      </div>
    );
  }
  return (
    <div className={`rounded-md border p-3 ${vram.passed ? 'border-border bg-muted/40' : 'border-red-200 bg-red-50'}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {vramPanelHeading(bytes)}
      </div>
      <div className={`mt-1 flex items-center gap-1.5 text-sm font-medium ${vram.passed ? 'text-foreground' : 'text-red-800'}`}>
        {/* Deliberately NOT a checkmark on pass — VRAM coverage is a sample
            of unknowable proportion; this neutral marker is the "different
            weight" the product review requires (see module header). */}
        {vram.passed ? <Circle className="size-3 fill-current" aria-hidden /> : <AlertTriangle className="size-4" aria-hidden />}
        {vram.passed ? vramPassLabel(bytes) : vramFailLabel(bytes)}
      </div>
      <p className={`mt-1 text-xs ${vram.passed ? 'text-muted-foreground' : 'text-red-700'}`}>
        {vram.passed ? vramPassDetail(bytes) : vramFailDetail(bytes)}
      </p>
      <p className="mt-1.5 text-[11px] text-muted-foreground">{vramFootnote(bytes)}</p>
    </div>
  );
}

/** Two separate panels, compute first — NEVER an aggregate pass/fail rollup
 * across the two (SIL-613 hard requirement). */
function VerificationChecksPanels({ checks }: { checks: VerificationChecksPresentation }) {
  return (
    <div className="mt-2 space-y-2">
      <ComputeCorrectnessPanel compute={checks.compute} />
      <VramPatternPanel vram={checks.vram} />
    </div>
  );
}

/**
 * SIL-688/SIL-689 — the multi-GPU honesty note + per-card table. WebGPU
 * returns exactly one adapter with no enumeration; v9's runner loop fixes
 * that by measuring every accelerator, so this renders a per-card verdict
 * list whenever the host has more than one GPU — the "N of M measured"
 * caveat banner is shown ONLY when coverage is incomplete (`multiGpuCaveat`
 * returns null once every device was measured, per SIL-689's requirement
 * that the caveat disappear at full coverage). Renders nothing when there's
 * no probe data or the host only has one GPU — never an empty box.
 */
function MultiGpuNote({ hardware }: { hardware: VerificationHardwarePresentation | null }) {
  if (!hardware || hardware.gpuCount <= 1) return null;
  const caveat = multiGpuCaveat(hardware);
  const hasLoopStatus = hardware.devices.some((d) => d.status !== undefined);
  return (
    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-amber-800">
        <Cpu className="size-3.5" aria-hidden />
        {caveat ??
          `This host reports ${hardware.gpuCount} GPUs - every one was measured. The worst card's result governs this run's verdict.`}
      </div>
      <ul className="mt-1.5 space-y-0.5 text-[11px] text-amber-700/90">
        {hardware.devices.map((d) => (
          <li key={d.index}>
            #{d.index} {d.vendor}
            {d.name ? ` ${d.name}` : ''} — serial {d.serial ?? 'unavailable'}
            {hasLoopStatus ? (
              <>
                {' '}
                — {d.status ?? 'not_run'}
                {d.status === 'measured' && d.verdict ? `: ${d.verdict}` : ''}
                {d.errorReason ? ` (${d.errorReason})` : ''}
              </>
            ) : (
              d.index === hardware.measuredIndex ? ' (measured)' : ''
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * SIL-585 (Wave 4/UI) — the ONE renderer for a synthesized Active
 * Verification outcome, shared by the passport §-section
 * (AssetPassportBody.tsx) and the customer portal's evidence-sources panel
 * (compact mode). "Synthesized onto the passport and listing, in the SAME
 * synthesized form" (§9) — the listing embeds the passport payload
 * (AssetPassport.tsx), so this component IS that shared form.
 *
 * Hard anti-laundering requirement (§9/§11 finding 6): the `tier3_self_reported`
 * branch renders NO badge/pill/checkmark — plain text in a dashed-border box
 * (this system's "missing/unverified" idiom, DESIGN.md's Dashed-Means-Missing
 * rule, repurposed here for "unverified" rather than "absent") with the
 * label VERBATIM, and shares zero classNames/icons with the tier12_* badge
 * treatment below it.
 */

const TIER_LABEL: Record<1 | 2, string> = { 1: 'Tier 1 (full hardware attestation)', 2: 'Tier 2 (polling & SKU validation)' };

export function VerificationOutcome({
  verification,
  compact = false,
}: {
  verification: PassportVerification;
  compact?: boolean;
}) {
  if (verification.kind === 'none') {
    return (
      <p className="text-sm text-muted-foreground">
        No Active Verification run recorded for this asset yet.
      </p>
    );
  }

  if (verification.kind === 'tier3_self_reported') {
    // NON-BADGE, explicitly labeled — never a pill, never ShieldCheck/
    // ShieldAlert iconography (that's the tier12_* badge vocabulary below).
    return (
      <>
        <div className="rounded-md border border-dashed border-border bg-muted/50 p-3">
          <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            <ShieldQuestion className="size-4 text-muted-foreground" aria-hidden />
            {TIER_3_SELF_REPORTED_LABEL}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {verification.passed
              ? 'The seller reports this test completed without in-band failures.'
              : `The seller-run test reported an issue: ${verificationFailureSymptom(verification.verdict)}.`}
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Ran {formatDate(verification.completedAt)} — no BMC hardware attestation available for
            this unit.
          </p>
        </div>
        <VerificationChecksPanels checks={verification.checks} />
        <MultiGpuNote hardware={verification.hardware} />
      </>
    );
  }

  if (verification.kind === 'tier12_failed') {
    return (
      <>
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <div className="flex items-center gap-1.5 text-sm font-medium text-red-800">
            <AlertTriangle className="size-4" aria-hidden />
            Verification failed: {verification.classification}
          </div>
          <p className="mt-1 text-xs text-red-700">{verificationFailureSymptom(verification.classification)}.</p>
          <p className="mt-1 text-[11px] text-red-700/80">
            {TIER_LABEL[verification.tier]} · {formatDate(verification.completedAt)}
          </p>
        </div>
        <VerificationChecksPanels checks={verification.checks} />
        <MultiGpuNote hardware={verification.hardware} />
      </>
    );
  }

  if (verification.kind === 'tier12_flagged') {
    return (
      <>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-center gap-1.5 text-sm font-medium text-amber-800">
            <AlertTriangle className="size-4" aria-hidden />
            Flagged: {verification.classification}
          </div>
          <p className="mt-1 text-xs text-amber-700">{verificationFailureSymptom(verification.classification)}.</p>
          <p className="mt-1 text-[11px] text-amber-700/80">
            {TIER_LABEL[verification.tier]} · {formatDate(verification.completedAt)}
          </p>
        </div>
        <VerificationChecksPanels checks={verification.checks} />
        <MultiGpuNote hardware={verification.hardware} />
      </>
    );
  }

  // tier12_pass
  return (
    <>
      <div
        className={`rounded-md border p-3 ${verification.stale ? 'border-border bg-muted' : 'border-emerald-200 bg-emerald-50'}`}
      >
        <div
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            verification.stale
              ? 'bg-muted text-muted-foreground'
              : 'bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-200'
          }`}
        >
          <ShieldCheck className="size-3.5" aria-hidden />
          {verification.verdict === 'pass_with_observations' ? 'Verified (with observations)' : 'Verified'}
        </div>
        {verification.passedOnRetry && (
          <div className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
            <RotateCcw className="size-3" aria-hidden /> Passed on retry
          </div>
        )}
        {!compact && (
          <p className="mt-1.5 text-xs text-muted-foreground">
            {TIER_LABEL[verification.tier]} hardware attestation.
          </p>
        )}
        <p className="mt-1 text-[11px] text-muted-foreground">
          Ran {formatDate(verification.completedAt)}
          {verification.stale ? ` — expired ${formatDate(verification.expiresAt)}` : ` · valid through ${formatDate(verification.expiresAt)}`}
        </p>
      </div>
      <VerificationChecksPanels checks={verification.checks} />
        <MultiGpuNote hardware={verification.hardware} />
    </>
  );
}
